import React from 'react';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';

interface PaginationControlsProps {
  currentPage: number;
  totalPages: number;
  pageSize: number | 'all';
  totalItems: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  loading: boolean;
  isLoadingAll: boolean;
  onNextPage: () => void;
  onPreviousPage: () => void;
  onFirstPage: () => void;
  onPageSizeChange: (size: number | 'all') => void;
  variant?: 'card' | 'inline';
}

const PaginationControls: React.FC<PaginationControlsProps> = ({
  currentPage,
  totalPages,
  pageSize,
  totalItems,
  hasNextPage,
  hasPreviousPage,
  loading,
  isLoadingAll,
  onNextPage,
  onPreviousPage,
  onFirstPage,
  onPageSizeChange,
  variant = 'card',
}) => {
  const startItem = pageSize === 'all' ? 1 : ((currentPage - 1) * (pageSize as number)) + 1;
  const endItem = pageSize === 'all' ? totalItems : Math.min(currentPage * (pageSize as number), totalItems);
  const evolutionLabel = totalItems === 1 ? 'evolução' : 'evoluções';
  const containerClassName =
    variant === 'inline'
      ? ''
      : 'bg-white border border-gray-200 rounded-lg p-4 mb-4';

  return (
    <div className={containerClassName}>
      <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <label htmlFor="pageSize" className="text-sm font-medium text-gray-700 whitespace-nowrap">
              Itens por página:
            </label>
            <select
              id="pageSize"
              value={pageSize}
              onChange={(e) => onPageSizeChange(e.target.value === 'all' ? 'all' : parseInt(e.target.value))}
              className="rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 text-sm"
              disabled={loading}
            >
              <option value="20">20</option>
              <option value="50">50 (padrão)</option>
              <option value="100">100</option>
              <option value="all">Carregar Todos</option>
            </select>
          </div>

        </div>

        <div className="flex items-center gap-3">
          <span className="text-sm text-gray-700">
            Exibindo <span className="font-medium">{startItem}</span> a{' '}
            <span className="font-medium">{endItem}</span> de{' '}
            <span className="font-medium">{totalItems}</span> {evolutionLabel}
          </span>

          {!isLoadingAll && (
            <div className="flex items-center gap-1">
              <button
                onClick={onFirstPage}
                disabled={!hasPreviousPage || loading}
                className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                title="Primeira página"
              >
                <ChevronsLeft className="w-4 h-4" />
              </button>

              <button
                onClick={onPreviousPage}
                disabled={!hasPreviousPage || loading}
                className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                title="Página anterior"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>

              <span className="px-3 py-2 text-sm font-medium text-gray-700 min-w-[80px] text-center">
                Página {currentPage}
                {totalPages > 0 && ` de ${totalPages}`}
              </span>

              <button
                onClick={onNextPage}
                disabled={!hasNextPage || loading}
                className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                title="Próxima página"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      </div>

      {!isLoadingAll && variant !== 'inline' && (
        <div className="mt-3 text-xs text-gray-500 text-center">
          Mostrando as últimas {pageSize} evoluções. Altere para ver mais ou menos registros por vez.
        </div>
      )}
    </div>
  );
};

export default PaginationControls;
